import { Button, FormGroup, Input, Label, Row, Select } from '@/components'
import { FlexWrapper } from '@/components/layout/flex/flexWrapper'
import { useAxios } from '@/context/axiosContext'
import { useDialog } from '@/context/dialogContext'
import { useRedis } from '@/hooks/useRedis'
import commonUtils from '@/utils/commonUtils'
import { useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { Col } from 'styled-bootstrap-grid'

type TInput = HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement

interface HolidayDetailItem {
  locdate: string
  dateName: string
  isHoliday: string
  dateKind: string
  batchYn: string
  useYn: string
  regId: string
  regDate: string
  updId: string
  updDate: string
}

const DetailHolidayMgmt: React.FC = () => {
  const axios = useAxios()
  const navigate = useNavigate()
  const location = useLocation()
  const { showAlert, showConfirm } = useDialog()
  const { getComCodes } = useRedis()
  const [requestData] = useState({
    locdate: location.state?.locdate || '',
  })

  const [data, setData] = useState<HolidayDetailItem>({
    locdate: '',
    dateName: '',
    isHoliday: 'Y',
    dateKind: '01',
    batchYn: 'N',
    useYn: 'Y',
    regId: '',
    regDate: '',
    updId: '',
    updDate: '',
  })

  const getHolidayDetail = async () => {
    try {
      const response = await axios.get('/holiday/getInfo', {
        params: {
          locdate: requestData.locdate,
        },
      })
      const holidayInfo = response.data.data.holidayInfo
      if (Array.isArray(holidayInfo)) {
        setData(holidayInfo[0])
      }
    } catch (error) {
      console.error('Error fetching getInfo info:', error)
    }
  }

  const handleInputChange = (e: React.ChangeEvent<TInput>) => {
    const { name, value } = e.target
    setData((prevState) => {
      return {
        ...prevState,
        [name]: value,
      } as HolidayDetailItem // 모든 필드를 포함하는 상태를 유지
    })
  }

  const validate = () => {
    const validateInfo: any = {
      locdate: {
        type: 's',
        require: true,
        name: '날짜',
        validator: (value: string) => {
          const dateRegex = /^\d{4}(0[1-9]|1[012])(0[1-9]|[12][0-9]|3[01])$/
          return dateRegex.test(value)
        },
      },
      dateName: {
        type: 's',
        require: false,
        name: '명칭',
      },
      isHoliday: {
        type: 's',
        require: true,
        name: '휴일여부',
      },
      dateKind: {
        type: 's',
        require: true,
        name: '특일종류',
      },
      batchYn: {
        type: 's',
        require: true,
        name: '배치여부',
      },
      useYn: {
        type: 's',
        require: true,
        name: '사용여부',
      },
    }
    let validateResult = true
    Object.entries(data).forEach(([key, value]) => {
      if (validateResult && validateInfo[key]) {
        const fieldInfo = validateInfo[key]
        if (fieldInfo.require && (!value || value.trim() === '')) {
          showValidationAlert(fieldInfo.name)
          validateResult = false
        } else if (fieldInfo.validator && !fieldInfo.validator(value)) {
          showAlert({
            type: 'warning',
            title: `${fieldInfo.name} 입력 포맷을 확인해 주세요.`,
          })
          validateResult = false
        }
      }
    })

    return validateResult
  }

  const showValidationAlert = (name: string) => {
    const code = name.charCodeAt(name.length - 1)
    const particle =
      code < 0xac00 || code > 0xd7a3 || (code - 0xac00) % 28 === 0 ? '를' : '을'
    showAlert({
      type: 'warning',
      title: `${name}${particle} 입력해주세요`,
    })
  }

  const updateHoliday = async () => {
    if (!validate()) {
      return
    }
    try {
      const response: any = await axios.post('/holiday/insert', data)
      if (response.data.common.resultCode == 'HOLIDAY0203') {
        showAlert({
          type: 'success',
          title: '휴일정보 저장을 성공했어요.',
        })
      } else {
        showAlert({
          title:
            response.data.common.resultMsg || '휴일정보 저장을 실패했어요.',
        })
      }
    } catch (error) {
      showAlert({
        title: '휴일정보 저장을 실패했어요.',
      })
      console.error('Error fetching holiday reg:', error)
    }
  }

  useEffect(() => {
    if (!!requestData.locdate) {
      getHolidayDetail()
    }
  }, [])
  return (
    <>
      <div style={{ width: '100%', height: '100%', marginTop: '50px' }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            width: '100%',
            marginBottom: '10px',
            alignItems: 'center',
            marginTop: '50px',
          }}
        >
          <p style={{ fontSize: '20px' }}>휴일등록/수정</p>
          <FlexWrapper gap="20px">
            <Button
              onClick={() =>
                showConfirm({
                  title: '휴일관리 화면으로 이동할까요?',
                  onConfirm() {
                    navigate('/holiday', {
                      state: {
                        prevSearchState: location.state?.prevSearchState,
                        locdate: location.state?.locdate || '',
                        page: location.state?.page || 1,
                        perPage: location.state?.perPage || 10,
                      },
                    })
                  },
                })
              }
              color="neverbe"
              variant="outlined"
            >
              목록
            </Button>
            <Button
              onClick={() =>
                showConfirm({
                  title: '휴일정보를 저장할까요?',
                  onConfirm() {
                    updateHoliday()
                  },
                })
              }
              color="neverbe"
            >
              저장
            </Button>
          </FlexWrapper>
        </div>
      </div>
      <Row>
        <Col col>
          <FormGroup mb="20px">
            <Label display="block" mb="5px" htmlFor="locdate">
              날짜
            </Label>
            <Input
              id="locdate"
              name="locdate"
              type="text"
              placeholder="YYYYMMDD"
              maxLength={8}
              value={data.locdate}
              disabled={!!requestData.locdate}
              onChange={handleInputChange}
            />
          </FormGroup>
        </Col>
        <Col col>
          <FormGroup mb="20px">
            <Label display="block" mb="5px" htmlFor="dateName">
              명칭
            </Label>
            <Input
              id="dateName"
              name="dateName"
              type="text"
              placeholder=""
              maxLength={50}
              value={data.dateName}
              onChange={handleInputChange}
            />
          </FormGroup>
        </Col>
      </Row>
      <Row>
        <Col col>
          <FormGroup mb="20px">
            <Label display="block" mb="5px" htmlFor="isHoliday">
              휴일여부
            </Label>
            <Select
              id="isHoliday"
              name="isHoliday"
              value={data.isHoliday || 'Y'}
              onChange={handleInputChange}
            >
              <option value={'Y'}>Y</option>
              <option value={'N'}>N</option>
            </Select>
          </FormGroup>
        </Col>
        <Col col>
          <FormGroup mb="20px">
            <Label display="block" mb="5px" htmlFor="dateKind">
              특일종류
            </Label>
            <Select
              id="dateKind"
              name="dateKind"
              value={data.dateKind}
              onChange={handleInputChange}
            >
              {getComCodes('DATE_KIND').map((item) => (
                <option key={item.code} value={item.code}>
                  {item.codeNm}
                </option>
              ))}
            </Select>
          </FormGroup>
        </Col>
        <Col col>
          <FormGroup mb="20px">
            <Label display="block" mb="5px" htmlFor="batchYn">
              배치여부
            </Label>
            <Select
              id="batchYn"
              name="batchYn"
              value={data.batchYn || 'N'}
              onChange={handleInputChange}
            >
              <option value={'Y'}>Y</option>
              <option value={'N'}>N</option>
            </Select>
          </FormGroup>
        </Col>
        <Col col>
          <FormGroup mb="20px">
            <Label display="block" mb="5px" htmlFor="useYn">
              사용여부
            </Label>
            <Select
              id="useYn"
              name="useYn"
              value={data.useYn || 'Y'}
              onChange={handleInputChange}
            >
              <option value={'Y'}>Y</option>
              <option value={'N'}>N</option>
            </Select>
          </FormGroup>
        </Col>
      </Row>
      {!!requestData.locdate ? (
        <>
          <Row>
            <Col col>
              <FormGroup mb="20px">
                <Label display="block" mb="5px" htmlFor="regId">
                  등록자
                </Label>
                <Input
                  id="regId"
                  name="regId"
                  type="text"
                  placeholder=""
                  maxLength={100}
                  value={data.regId}
                  disabled={true}
                />
              </FormGroup>
            </Col>
            <Col col>
              <FormGroup mb="20px">
                <Label display="block" mb="5px" htmlFor="regDate">
                  등록일시
                </Label>
                <Input
                  id="regDate"
                  name="regDate"
                  type="text"
                  placeholder=""
                  maxLength={100}
                  value={commonUtils.formatDate(data.regDate, true)}
                  disabled={true}
                />
              </FormGroup>
            </Col>
            <Col col>
              <FormGroup mb="20px">
                <Label display="block" mb="5px" htmlFor="updId">
                  수정자
                </Label>
                <Input
                  id="updId"
                  name="updId"
                  type="text"
                  placeholder=""
                  maxLength={100}
                  value={data.updId}
                  disabled={true}
                />
              </FormGroup>
            </Col>
            <Col col>
              <FormGroup mb="20px">
                <Label display="block" mb="5px" htmlFor="updDate">
                  수정일시
                </Label>
                <Input
                  id="updDate"
                  name="updDate"
                  type="text"
                  placeholder=""
                  maxLength={100}
                  value={commonUtils.formatDate(data.updDate, true)}
                  disabled={true}
                />
              </FormGroup>
            </Col>
          </Row>
        </>
      ) : (
        <></>
      )}
    </>
  )
}
export default DetailHolidayMgmt
